import type {
  ActionPriority,
  BoardViewMode,
  DecisionAction,
  DecisionFiltersState,
  LocalActionFlags,
  SortMode,
} from './types';
import { PRIORITY_ORDER } from './types';

export function defaultFilters(): DecisionFiltersState {
  return {
    priority: 'all',
    workspace: 'all',
    businessArea: 'all',
    dateFrom: '',
    dateTo: '',
    seller: 'all',
    marketplace: 'all',
    search: '',
  };
}

function inDateRange(iso: string, from: string, to: string): boolean {
  if (!from && !to) return true;
  const t = Date.parse(iso);
  if (!Number.isFinite(t)) return true;
  if (from) {
    const f = Date.parse(from);
    if (Number.isFinite(f) && t < f) return false;
  }
  if (to) {
    const end = Date.parse(to);
    if (Number.isFinite(end)) {
      // inclusive day end when date-only
      const upper = to.length <= 10 ? end + 86_400_000 - 1 : end;
      if (t > upper) return false;
    }
  }
  return true;
}

export function applyFilters(
  actions: DecisionAction[],
  filters: DecisionFiltersState,
  flags: LocalActionFlags,
  view: BoardViewMode,
): DecisionAction[] {
  const completed = new Set(flags.completed);
  const dismissed = new Set(flags.dismissed);
  const bookmarked = new Set(flags.bookmarked);
  const pinned = new Set(flags.pinned);
  const q = filters.search.trim().toLowerCase();

  return actions.filter((a) => {
    if (view === 'active') {
      if (completed.has(a.id) || dismissed.has(a.id)) return false;
    } else if (view === 'completed') {
      if (!completed.has(a.id)) return false;
    } else if (view === 'bookmarks') {
      if (!bookmarked.has(a.id)) return false;
      if (dismissed.has(a.id) && !completed.has(a.id)) return false;
    } else if (view === 'pinned') {
      if (!pinned.has(a.id)) return false;
      if (completed.has(a.id) || dismissed.has(a.id)) return false;
    }

    if (filters.priority !== 'all' && a.priority !== filters.priority) return false;
    if (filters.workspace !== 'all' && a.sourceWorkspace !== filters.workspace)
      return false;
    if (filters.businessArea !== 'all' && a.businessArea !== filters.businessArea)
      return false;
    if (filters.seller !== 'all') {
      const sellerKey = a.sellerId || a.sellerLabel || '';
      if (sellerKey !== filters.seller) return false;
    }
    if (filters.marketplace !== 'all' && (a.marketplace || '') !== filters.marketplace)
      return false;
    if (!inDateRange(a.updatedAt, filters.dateFrom, filters.dateTo)) return false;

    if (q) {
      const hay = [
        a.title,
        a.description,
        a.evidence,
        a.businessArea,
        a.sourceWorkspace,
        a.sellerLabel,
        a.marketplace,
      ]
        .filter(Boolean)
        .join(' ')
        .toLowerCase();
      if (!hay.includes(q)) return false;
    }
    return true;
  });
}

export function sortActions(
  actions: DecisionAction[],
  mode: SortMode,
  flags: LocalActionFlags,
): DecisionAction[] {
  const pinned = new Set(flags.pinned);
  const pRank = (p: ActionPriority) => PRIORITY_ORDER.indexOf(p);
  const copy = [...actions];
  copy.sort((a, b) => {
    const ap = pinned.has(a.id) ? 0 : 1;
    const bp = pinned.has(b.id) ? 0 : 1;
    if (ap !== bp) return ap - bp;

    if (mode === 'confidence_asc') return a.confidence - b.confidence;
    if (mode === 'confidence_desc') return b.confidence - a.confidence;

    const ta = Date.parse(a.updatedAt) || 0;
    const tb = Date.parse(b.updatedAt) || 0;
    if (tb !== ta) return tb - ta;
    return pRank(a.priority) - pRank(b.priority);
  });
  return copy;
}

export function groupByPriority(
  actions: DecisionAction[],
): Record<ActionPriority, DecisionAction[]> {
  const g: Record<ActionPriority, DecisionAction[]> = {
    critical: [],
    important: [],
    opportunity: [],
    information: [],
  };
  for (const a of actions) g[a.priority].push(a);
  return g;
}

export function uniqueOptions(actions: DecisionAction[]): {
  workspaces: string[];
  sellers: { value: string; label: string }[];
  marketplaces: string[];
} {
  const workspaces = new Set<string>();
  const sellers = new Map<string, string>();
  const marketplaces = new Set<string>();
  for (const a of actions) {
    workspaces.add(a.sourceWorkspace);
    if (a.sellerId || a.sellerLabel) {
      const v = a.sellerId || a.sellerLabel || '';
      sellers.set(v, a.sellerLabel || v);
    }
    if (a.marketplace) marketplaces.add(a.marketplace);
  }
  return {
    workspaces: [...workspaces].sort(),
    sellers: [...sellers.entries()].map(([value, label]) => ({ value, label })),
    marketplaces: [...marketplaces].sort(),
  };
}
